import { TailwindColor, tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { TFunction } from 'i18next'

import { ReviewColumnAggregate } from '../../../api/end-of-journey-reviews/centre/types'
import {
  EndOfJourneyReviewsSummaryFilter,
  EndOfJourneyReviewsSummaryResponse,
  SummaryCentreRow,
  SummaryCountryGroup
} from '../../../api/end-of-journey-reviews/summary/types'
import { formatReviewNumber } from '../../../components/pages/ols/centers/center-tab-views/center-end-of-stay-appreciations/review-rating-pill'

/** Defaults of the panel: current year, /5 filter with 5 and 4 stars, /3 filter not applied. */
export const getSummaryInitialFilters = (): EndOfJourneyReviewsSummaryFilter => ({
  year: new Date().getFullYear(),
  includeOto: false,
  fiveStars: [5, 4],
  threeStars: []
})

// An empty array and a missing value both mean "not applied"
const isUnset = (value: unknown) =>
  value === undefined || value === null || (Array.isArray(value) && value.length === 0)

const isSameFilterValue = (value: unknown, defaultValue: unknown) => {
  // An unticked toggle is the same as a missing one
  if (typeof defaultValue === 'boolean') return (value ?? false) === defaultValue
  if (isUnset(value) || isUnset(defaultValue)) return isUnset(value) && isUnset(defaultValue)
  if (Array.isArray(value) && Array.isArray(defaultValue))
    return (
      value.length === defaultValue.length && value.every((item) => defaultValue.includes(item))
    )
  return value === defaultValue
}

/**
 * "Filtrer par" counter: the filters that differ from the defaults (clearing the year or every /5
 * star counts too), so the button is not highlighted on first load.
 */
export const countSummaryFilters = (filter: EndOfJourneyReviewsSummaryFilter | undefined) => {
  if (!filter) return 0
  const defaults = getSummaryInitialFilters()
  const keys = new Set([...Object.keys(defaults), ...Object.keys(filter)]) as Set<
    keyof EndOfJourneyReviewsSummaryFilter
  >
  return [...keys].filter((key) => !isSameFilterValue(filter[key], defaults[key])).length
}

/** Which figure of a column a totals row shows. */
export type SummaryFigureKind = 'answers' | 'responseRate' | 'average'

export type SummaryRow = {
  id: number
  rowColor?: TailwindColor
} & (
  | { kind: 'general'; figure: SummaryFigureKind; label: string; first: boolean }
  | { kind: 'country'; group: SummaryCountryGroup; countryKey: string }
  | { kind: 'centre'; centre: SummaryCentreRow; countryKey: string }
  | {
      kind: 'subtotal'
      figure: Exclude<SummaryFigureKind, 'answers'>
      label: string
      group: SummaryCountryGroup
    }
)

export const getCountryKey = (group: SummaryCountryGroup) => `${group.country?.id ?? 'none'}`

/** A column's figure as shown in a pill; null shows "n/a". */
export const formatSummaryFigure = (
  aggregate: ReviewColumnAggregate | undefined,
  figure: SummaryFigureKind
): string | null => {
  if (!aggregate) return null
  if (figure === 'answers') return aggregate.answered ? `${aggregate.answered}` : null
  if (figure === 'responseRate')
    return aggregate.responseRate === null ? null : formatReviewNumber(aggregate.responseRate)
  return aggregate.average === null
    ? null
    : `${formatReviewNumber(aggregate.average)}/${aggregate.scaleMax}`
}

/**
 * Flat rows of the table: the "TOTAL GÉNÉRAL" block, then per country its header row and, when
 * expanded, its centre rows and its two subtotal rows, countries in the backend's "Pays" order.
 * `expandAll` is used to print.
 */
export const buildSummaryRows = (
  data: EndOfJourneyReviewsSummaryResponse,
  expanded: Set<string>,
  t: TFunction<AvailableLanguages, undefined>,
  expandAll = false
): SummaryRow[] => {
  const totalColor = tailwindColors.secondary.light.DEFAULT
  const groupColor = tailwindColors.grey.light.DEFAULT

  const rows: SummaryRow[] = [
    {
      id: -1,
      kind: 'general',
      figure: 'answers',
      label: t('end.of.stay.appreciations.summary.rows.answers'),
      first: true,
      rowColor: totalColor
    },
    {
      id: -2,
      kind: 'general',
      figure: 'responseRate',
      label: t('end.of.stay.appreciations.summary.rows.response.rate'),
      first: false,
      rowColor: totalColor
    },
    {
      id: -3,
      kind: 'general',
      figure: 'average',
      label: t('end.of.stay.appreciations.summary.rows.average'),
      first: false,
      rowColor: totalColor
    }
  ]

  data.countries.forEach((group, index) => {
    const countryKey = getCountryKey(group)
    const baseId = -(1000 + index * 10)
    rows.push({ id: baseId, kind: 'country', group, countryKey })
    if (!expandAll && !expanded.has(countryKey)) return

    rows.push(
      ...group.centres.map(
        (centre): SummaryRow => ({
          id: centre.id,
          kind: 'centre',
          centre,
          countryKey,
          rowColor: groupColor
        })
      ),
      {
        id: baseId - 1,
        kind: 'subtotal',
        figure: 'average',
        label: t('end.of.stay.appreciations.summary.rows.enrolled.and.average'),
        group,
        rowColor: groupColor
      },
      {
        id: baseId - 2,
        kind: 'subtotal',
        figure: 'responseRate',
        label: t('end.of.stay.appreciations.summary.rows.response.rate'),
        group,
        rowColor: groupColor
      }
    )
  })

  return rows
}

/**
 * Opens the print window right away (in the click, so the browser does not block it); the table is
 * written into it with writePrintWindow once the data is loaded. null when the browser blocked it.
 */
export const openPrintWindow = (title: string, loadingText: string): Window | null => {
  const printWindow = window.open('', '_blank')
  if (!printWindow) return null

  printWindow.document.write(
    `<html><head><title>${title}</title></head><body>${loadingText}</body></html>`
  )
  printWindow.document.close()
  return printWindow
}

/** Writes the table markup with the app styles into the print window, prints it, then closes it. */
export const writePrintWindow = (printWindow: Window, html: string, title: string) => {
  // Closed by the user while the data was loading
  if (printWindow.closed) return

  const styles = [...document.querySelectorAll('link[rel="stylesheet"], style')]
    .map((node) => node.outerHTML)
    .join('')

  printWindow.document.open()
  printWindow.document.write(
    `<html><head><title>${title}</title>${styles}<style>body{padding:16px;-webkit-print-color-adjust:exact;print-color-adjust:exact}</style></head><body>${html}</body></html>`
  )
  printWindow.document.close()

  // Closed once the print dialog is done, never right after print()
  printWindow.addEventListener('afterprint', () => printWindow.close())
  const print = () => {
    printWindow.focus()
    printWindow.print()
  }
  // The copied stylesheets must be loaded before printing
  if (printWindow.document.readyState === 'complete') print()
  else printWindow.addEventListener('load', print)
}
